// The board side of movement (qa-runs#1161; design/12 §4 "Reachable squares"): the area a token may reach, worked out
// from what THIS screen holds (the DM's every zone and wall; a player's `fog.difficult` and the walls they were sent),
// the picker's kind, and the move that waits for Confirm after the drop. Only the mover's screen has any of it: nothing
// here is sent until Confirm, and the server's count of the confirmed move is the final word.
import {
  areaInputs, boardReach, chargeBands, chargeEnds, turnMoves, dispositionOf, isGridless, movePlan, moveOfLabel, passableFor, reachEdges, reachSquares, tallyActions,
  type ActionRules, type ActionSpend, type BoardReach, type DistanceRules, type MovementRules, type Extent, type Grid, type MovePlan, type PlayerEncounter, type Point, type Span, type Values,
} from '@dragons-table/vtt-shared';
import type { PaletteName } from '@dragons-table/vtt-shared';

/** A thing as the area reads it: where it stands, its size, side and numbers. */
export interface AreaThing { id: string; kind: string; x: number; y: number; size: { w: number; h: number }; values?: Partial<Values>; disposition?: 'friend' | 'neutral' | 'foe'; pc?: boolean; owners?: readonly string[]; dead?: boolean; floor?: string }

export interface AreaState {
  objects: Record<string, AreaThing>;
  zones?: ReadonlyArray<{ kind: string; spans: readonly Span[] }> | undefined;
  fog?: { difficult?: Span[] } | null | undefined;
  walls?: ReadonlyArray<{ a: Point; b: Point; move?: boolean; sight?: boolean; door?: { open: boolean } }> | undefined;
  scene: { freeMove?: true };
}

export interface AreaArgs {
  state: AreaState;
  encounter: Pick<PlayerEncounter, 'running' | 'active' | 'combatants' | 'turn'> | null;
  id: string;
  grid: Grid; extent: Extent; distance: DistanceRules | null;
  /** The system's reach, for where a charge ends. */
  reach?: Parameters<typeof chargeBands>[0] | undefined;
  /** The system's actions, for whether the turn holds another Move. */
  actions?: ActionRules | null | undefined;
  pick?: string | undefined;
}

/**
 * The fight as the area reads it for one token: null outside one; else whether it acts now, what is left of its Move,
 * and whether it has moved or taken a 5-foot step this turn (slice B; `turnMoves`, by the system's kinds).
 */
export function fightFor(enc: AreaArgs['encounter'], id: string, movement?: MovementRules, actions?: ActionRules | null): { acting: boolean; moveLeft?: number; moved?: boolean; stepped?: boolean; fresh?: boolean; anotherMove?: boolean } | null {
  if (!enc?.running) return null;
  const c = enc.combatants.find((x) => x.tokens.includes(id));
  const acting = !!c && enc.active === c.id;
  const turn = acting && enc.turn?.combatant === c!.id ? enc.turn : null;
  const left = turn?.moveLeft;
  const { moved, stepped } = turnMoves(turn, movement);
  const fresh = acting && !moved && !stepped && !turn?.spent.length;
  // The first kind's one action still in the turn, by the count the strip shows (3.5: a standard taken as a second move).
  const spends = movement?.kinds[0]?.spends;
  const another = !!turn && !!actions && spends?.length === 1 && (tallyActions(actions, turn.spent as ActionSpend[]).find((r) => r.key === spends[0])?.left ?? 0) > 0;
  return { acting, ...(left !== undefined ? { moveLeft: left } : {}), ...(moved ? { moved } : {}), ...(stepped ? { stepped } : {}), ...(fresh ? { fresh } : {}), ...(another ? { anotherMove: true } : {}) };
}

/** A token's plan and its area on this screen, or null when it is no token. */
export function tokenArea(a: AreaArgs): { plan: MovePlan; reach: BoardReach | null } | null {
  const o = a.state.objects[a.id];
  if (!o || o.kind !== 'token') return null;
  const plan = movePlan({ freeMove: a.state.scene.freeMove === true, distance: a.distance, gridless: isGridless(a.grid), values: o.values ?? {}, fight: fightFor(a.encounter, a.id, a.distance?.movement, a.actions), pick: a.pick });
  if (plan.mode !== 'kind' && plan.mode !== 'rest') return { plan, reach: null };
  const side = dispositionOf(o);
  const { difficult, walls } = areaInputs(a.state);
  // A charge (qa-runs#366): only the squares it may end in.
  if (plan.kind.charge) return { plan, reach: chargeEnds({ token: o, things: Object.values(a.state.objects), grid: a.grid, extent: a.extent, distance: a.distance!, difficult, walls, budget: plan.budget, rule: plan.kind.charge, bands: a.reach ? chargeBands(a.reach, o) : [] })?.reach ?? null };
  const others = Object.values(a.state.objects).filter((x) => x.id !== o.id && x.kind === 'token').map((x) => ({ id: x.id, x: x.x, y: x.y, size: x.size, passable: passableFor(side, dispositionOf(x)) }));
  const reach = boardReach({ token: o, others, grid: a.grid, extent: a.extent, distance: a.distance!, difficult, walls, budget: plan.budget, noDifficult: plan.kind.difficult === false });
  return { plan, reach };
}

/** The area's colours in this person's look: a light green fill and a darker red edge; the colour-blind palette its own. */
export function areaColours(palette: PaletteName, theme: 'dark' | 'light'): { fill: number; edge: number; alpha: number } {
  if (palette === 'okabe-ito') return { fill: 0x009e73, edge: theme === 'light' ? 0x8f3f00 : 0xd55e00, alpha: 0.22 };
  return { fill: 0x4ade80, edge: theme === 'light' ? 0x7f1d1d : 0xb91c1c, alpha: 0.22 };
}

/** Draws an area: its squares lightly filled (the map shows through), a darker red line round its edge. */
export function drawArea(g: { clear(): unknown; rect(x: number, y: number, w: number, h: number): unknown; fill(s: unknown): unknown; moveTo(x: number, y: number): unknown; lineTo(x: number, y: number): unknown; stroke(s: unknown): unknown }, reach: BoardReach | null, colours: { fill: number; edge: number; alpha: number }, zoom: number): void {
  g.clear();
  if (!reach) return;
  const squares = reachSquares(reach);
  for (const k of squares) {
    const [i, j] = k.split(',').map(Number) as [number, number];
    g.rect(reach.xs[i]!, reach.ys[j]!, reach.xs[i + 1]! - reach.xs[i]!, reach.ys[j + 1]! - reach.ys[j]!);
  }
  if (squares.size) g.fill({ color: colours.fill, alpha: colours.alpha });
  const edges = reachEdges(squares, reach.xs, reach.ys);
  for (const [x0, y0, x1, y1] of edges) { g.moveTo(x0, y0); g.lineTo(x1, y1); }
  if (edges.length) g.stroke({ color: colours.edge, width: 2.5 / zoom, alpha: 0.95 });
}

// ------------------------------------------------------------------------------------------------ the store

/** A move dropped and waiting: its words, and what Confirm and Cancel do (the canvas's). */
/**
 * A move dropped and waiting: its words, and what Confirm and Cancel do (the canvas's). Floors (qa-runs#1160): `floors`,
 * the floors it may step onto where it ends (a stair or opening); `height`, its height and the floor's base and top in
 * squares, for the +/− control — Confirm sends the floor and height chosen, and the server counts the climb.
 */
export interface PendingMove {
  id: string; label: string; confirm: (extra?: { floor?: string; z?: number }) => void; cancel: () => void;
  floors?: Array<{ id: string; name: string }>;
  height?: { z: number; perSquare: number; unit: string };
}

export interface MoveView {
  /** The token whose area shows (selected or picked up), its plan; null when none. */
  token: string | null; plan: MovePlan | null;
  /** The kind picked for that token (its key); null: the first. */
  pick: string | null;
  pending: PendingMove | null;
}

type Listener = (v: MoveView) => void;
let view: MoveView = { token: null, plan: null, pick: null, pending: null };
const listeners = new Set<Listener>();
const emit = (next: MoveView) => { view = next; for (const l of listeners) l(view); };

/** The movement store the canvas writes and the picker and confirmation read. */
export const moveStore = {
  get: (): MoveView => view,
  subscribe(l: Listener): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
  /** The token and plan the area shows; a new token starts at the first kind again. */
  show(token: string | null, plan: MovePlan | null): void {
    if (token === view.token && JSON.stringify(plan) === JSON.stringify(view.plan)) return;
    emit({ ...view, token, plan, pick: token === view.token ? view.pick : null });
  },
  pickKind(key: string): void { emit({ ...view, pick: key }); },
  setPending(p: PendingMove | null): void { emit({ ...view, pending: p }); },
  reset(): void { emit({ token: null, plan: null, pick: null, pending: null }); },
};

/** The confirmation's words for a route that costs `squares`: "Move: 25 of 30 ft". */
export function pendingLabel(plan: MovePlan, squares: number, distance: DistanceRules): string | null {
  if (plan.mode !== 'kind' && plan.mode !== 'rest') return null;
  return moveOfLabel(plan.kind.label, squares, plan.budget, distance);
}

/** The body Confirm sends: the move with its kind, or as the rest of a Move. */
export function confirmedMove(plan: MovePlan, base: { id: string; route: Point[]; rev: number }): { kind: 'object.move'; id: string; route: Point[]; rev: number; move?: string; rest?: true } {
  if (plan.mode === 'kind') return { kind: 'object.move', ...base, move: plan.kind.key };
  if (plan.mode === 'rest') return { kind: 'object.move', ...base, move: plan.kind.key, rest: true };
  return { kind: 'object.move', ...base };
}
